import { SubmitJobRequest } from "../types";
import { API_BASE_URL } from "../config";

const apiUrl = `${API_BASE_URL}/api`;

/**
 * Submits a new job to the backend.
 *
 * @param jobRequest The complete job configuration, including job_type.
 * @param accessToken The user's Microsoft Entra ID access token.
 * @returns The response from the backend, typically including the new job's ID.
 */
export async function submitJob(jobRequest: SubmitJobRequest, accessToken: string): Promise<any> {
  // We construct the full URL for the generic job submission endpoint.
  const url = `${apiUrl}/jobs/submit-job`;

  // We prepare the options for the fetch call.
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(jobRequest),
  };
  // Security: do not log request URLs/bodies/identifiers in production.
  // (URLs/bodies may contain folder URLs, drive/item IDs, and other sensitive metadata.)

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      // If the server returns an error, we read the response as text, as it might not be JSON.
      const errorBody = await response.text();
      throw new Error(
        `Failed to submit job. Server responded with ${response.status}: ${errorBody}`
      );
    }

    // If successful, we parse and return the JSON response from the server.
    return response.json();
  } catch (error) {
    // This catch block handles network errors.
    console.error("A network error occurred during the submitJob API call:", error);
    throw new Error(
      `Network request failed. Please ensure the backend server is running and accessible.`
    );
  }
}

/**
 * Fetches the status of a specific job from the backend.
 *
 * @param batchId The ID of the job to check.
 * @param accessToken The user's Microsoft Entra ID access token.
 * @returns The job status details from the backend.
 */
export async function getJobStatus(batchId: string, accessToken: string): Promise<any> {
  const url = `${apiUrl}/jobs/status/${batchId}`;

  const options = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(
        `Failed to get job status. Server responded with ${response.status}: ${errorBody}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("A network error occurred during the getJobStatus API call:", error);
    throw new Error(
      `Network request failed. Please ensure the backend server is running and accessible.`
    );
  }
}

/**
 * Lists recent jobs for the current user (stateless rehydration support).
 *
 * @param accessToken Backend-scoped access token.
 * @param limit Maximum number of jobs to return (default 50).
 */
export async function listJobs(accessToken: string, limit: number = 50): Promise<any[]> {
  const url = `${apiUrl}/jobs/list?limit=${encodeURIComponent(String(limit))}`;

  const options = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  } as const;

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(
        `Failed to list jobs. Server responded with ${response.status}: ${errorBody}`
      );
    }
    return response.json();
  } catch (error) {
    console.error("A network error occurred during the listJobs API call:", error);
    throw new Error(
      `Network request failed. Please ensure the backend server is running and accessible.`
    );
  }
}

/**
 * Best-effort warmup: asks the backend to enqueue a non-sensitive warmup message so the worker
 * can spin up ahead of a real job submission.
 *
 * This should be called after login/registration succeeds. It is safe to ignore failures.
 */
export async function warmupWorker(accessToken: string): Promise<void> {
  const url = `${apiUrl}/jobs/warmup`;

  const options = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  } as const;

  try {
    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info("[warmup] Sending worker warmup request:", url);
    }

    // Best-effort: do not throw on non-OK; this should not block UX.
    const resp = await fetch(url, options);
    let body: any = undefined;
    try {
      body = await resp.json();
    } catch {
      body = undefined;
    }

    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info("[warmup] Warmup response:", resp.status, body);
    }
  } catch {
    // Silent failure: the warmup is an optimisation only.
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn("[warmup] Warmup request failed (ignored).");
    }
  }
}
